import moment from "moment";

import { LogSortColumn } from "@/Administration/Models/Log/LogSortColumn";
import { LogIntervalType } from "@/Administration/Models/UI/Enums/Administration";
import { AdministrationStore } from "@/Administration/Stores/AdministrationStore";
import { MyleHelper } from "@/Common/Helpers/MyleHelper";
import { EmployeeUIModel } from "@/Common/Models/UI/Employee/EmployeeUIModel";
import { SortDirection } from "@/Common/Models/UI/Enums";
import { CheckboxTarget } from "@/Common/Models/UI/Inputs/CheckboxTarget";
import { CheckboxUIModel } from "@/Common/Models/UI/Inputs/CheckboxUIModel";
import { MyleTableTarget } from "@/Common/Models/UI/Tables/MyleTableTarget";
import * as LocalOperations from "@/Common/Operations/operations.local";
import { LocalEvent } from "@/Common/Stores/Actions/LocalEvent";
import { BaseOperationHandler } from "@/Common/Stores/Handlers/Local/BaseOperationHandler";

class AdministrationLogHandler extends BaseOperationHandler {
    private readonly helper: MyleHelper;

    constructor() {
        super();
        this.helper = new MyleHelper();
        this.RegisterOperation(LocalOperations.AdministrationLog.GetAllLog, this.loadAdminLogData);
        this.RegisterOperation(LocalOperations.AdministrationLog.RefreshGrid, this.loadAdminLogData);
        this.RegisterOperation(LocalOperations.AdministrationLog.SearchSettings_SearchTextChanged, this.handleSearchTextChanged);
        this.RegisterOperation(LocalOperations.AdministrationLog.SearchSettings_Open, this.handleOpenSearchSettings);
        this.RegisterOperation(LocalOperations.AdministrationLog.SearchSettings_Close, this.handleCloseSearchSettings);
        this.RegisterOperation(LocalOperations.AdministrationLog.SearchSettings_ContainerCheckboxChange, this.handleSearchSettingsCheckedChanged);
        this.RegisterOperation(LocalOperations.AdministrationLog.SearchSettings_OkClick, this.handleSearchSettingsOkClick);
        this.RegisterOperation(LocalOperations.AdministrationLog.SearchSettings_FromDateChanged, this.handleFromDateChanged);
        this.RegisterOperation(LocalOperations.AdministrationLog.SearchSettings_ToDateChanged, this.handleToDateChanged);
        this.RegisterOperation(LocalOperations.AdministrationLog.SearchSettings_UserFilterTextChanged, this.handleUserFilterTextChanged);
        this.RegisterOperation(LocalOperations.AdministrationLog.SearchSettings_ActionFilterTextChanged, this.handleActionFilterTextChanged);
        this.RegisterOperation(LocalOperations.AdministrationLog.SearchSettings_NbAccessTextChanged, this.handleNbAccessTextChanged);
        this.RegisterOperation(LocalOperations.AdministrationLog.SearchSettings_IntervalTextChanged, this.handleIntervalTextChanged);
        this.RegisterOperation(LocalOperations.AdministrationLog.SearchSettings_IntervalTypeChanged, this.handleIntervalTypeChanged);

        this.RegisterOperation(LocalOperations.AdministrationLog.CurrentPageChange, this.handlePageChange);
        this.RegisterOperation(LocalOperations.AdministrationLog.SearchSettings_OnResultsPerPageChange, this.handleResulstPerPageChange);

        this.RegisterOperation(LocalOperations.Misc.Table_ColumnSortClick, this.columnSort);
        this.RegisterOperation(LocalOperations.Misc.MyleApp_Container_Click, this.handleContainerClick);

        this.RegisterOperation(LocalOperations.AdministrationLog.SearchSettings_ColumnOrderingPopup_Open, this.columnOrderingPopup_Open);

        this.RegisterOperation(LocalOperations.AdministrationLog.OntarioCdsExport_Open, this.handleOpenOntarioCdsExport);
        this.RegisterOperation(LocalOperations.AdministrationLog.OntarioCdsExport_Close, this.handleCloseOntarioCdsExport);

        this.RegisterOperation(LocalOperations.AdministrationLog.OntarioCdsImport_Open, this.handleOpenOntarioCdsImport);
        this.RegisterOperation(LocalOperations.AdministrationLog.OntarioCdsImport_Close, this.handleCloseOntarioCdsImport);
    }

    private loadAdminLogData(request: LocalEvent, store: AdministrationStore): void {
        if (!store.areLogDateFieldsValid()) {
            store.logTabModel = store.logTabModel.set("DateFrom", null);
            store.logTabModel = store.logTabModel.set("DateTo", null);
        }

        const today = new Date();
        if (!store.logTabModel.get("DateFrom"))
            store.logTabModel = store.logTabModel.set("DateFrom", today);
        if (!store.logTabModel.get("DateTo"))
            store.logTabModel = store.logTabModel.set("DateTo", today);

        if (!store.areLogAccessReportFieldsValid()) {
            store.logTabModel = store.logTabModel.set("NbAccess", null);
            store.logTabModel = store.logTabModel.set("Interval", null);
        }

        let firstLoad = request.Get<boolean>("firstLoad");

        if (firstLoad) {
            store.loadUsersUICache();
            store.loadActionTypeCache();
            store.logTabModel = store.logTabModel.set("SelectedPageNumber", 1);
        }
        else {
            store.logTabModel = store.logTabModel.set("ActionTypeCache", store.loadFilteredActionsBasedOn(store.logTabModel.get("ActionTypeCache")));
        }
        store.logTabAction.loadAdminLogData(store.logTabModel);
    }

    private handleSearchTextChanged(request: LocalEvent, store: AdministrationStore): void {
        let text = request.Get<string>("filterText");
        store.logTabModel = store.logTabModel.set("LogSearchText", text);

        window.setTimeout(() => {
            this.loadAdminLogData(request, store);
        }, 250);
    }

    private handleOpenSearchSettings(request: LocalEvent, store: AdministrationStore): void {
        store.logTabModel = store.logTabModel.set("ShowLogDisplaySettings", true);
    }

    private handleCloseSearchSettings(request: LocalEvent, store: AdministrationStore): void {
        store.logTabModel = store.logTabModel.set("ShowLogDisplaySettings", false);
    }

    private handleSearchSettingsCheckedChanged(request: LocalEvent, store: AdministrationStore): void {
        var target = request.Get<CheckboxTarget>("target");
        var checked = request.Get<boolean>("checked");
        var id = request.Get<string>("id");

        switch (target) {
            case CheckboxTarget.Myle_Admin_Log_Users_AllCheck:
                store.logTabModel = store.logTabModel.set("SelectAllUsersChecked", checked);
                store.logTabModel = store.logTabModel.set("UsersFiltered", this.helper.ChangeCheckStateFromAllICheckable(store.logTabModel.get("UsersFiltered"), checked));
                break;

            case CheckboxTarget.Myle_Admin_Log_Users_Check:
                store.logTabModel = store.logTabModel.set("SelectAllUsersChecked", false);
                store.logTabModel = store.logTabModel.set("UsersGlobalCache", this.helper.FindUpdateICheckableItem<EmployeeUIModel>(store.logTabModel.get("UsersGlobalCache"), id, checked));
                store.logTabModel = store.logTabModel.set("UsersFiltered", store.getUsersBasedOnQuery());
                break;

            case CheckboxTarget.Myle_Admin_Log_ActionTypes_AllCheck:
                store.logTabModel = store.logTabModel.set("SelectAllActionTypesChecked", checked);
                store.logTabModel = store.logTabModel.set("ActionTypeFiltered", this.helper.ChangeCheckStateFromAllICheckable(store.logTabModel.get("ActionTypeFiltered"), checked));
                break;

            case CheckboxTarget.Myle_Admin_Log_ActionTypes_Check:
                store.logTabModel = store.logTabModel.set("SelectAllActionTypesChecked", false);
                store.logTabModel = store.logTabModel.set("ActionTypeCache", this.helper.FindUpdateICheckableItem<CheckboxUIModel>(store.logTabModel.get("ActionTypeCache"), id, checked));
                store.logTabModel = store.logTabModel.set("ActionTypeFiltered", store.getActionsBasedOnQuery());
                break;

            case CheckboxTarget.Myle_Admin_Log_AccessReport_Check:
                let defaultValue: number = 5;
                store.logTabModel = store.logTabModel.set("IsAccessReportSelected", checked);

                if (!store.logTabModel.get("NbAccess"))
                    store.logTabModel = store.logTabModel.set("NbAccess", defaultValue);
                if (!store.logTabModel.get("Interval"))
                    store.logTabModel = store.logTabModel.set("Interval", defaultValue);
                break;

            default:
                break;
        }
    }

    private handleSearchSettingsOkClick(request: LocalEvent, store: AdministrationStore): void {
        store.logTabModel = store.logTabModel.set("SelectedPageNumber", 1);
        store.logTabModel = store.logTabModel.set("ShowLogDisplaySettings", false);

        if (!store.areLogAccessReportFieldsValid() || !store.areLogDateFieldsValid())
            return;

        this.loadAdminLogData(request, store);
    }

    private handleFromDateChanged(request: LocalEvent, store: AdministrationStore): void {
        let date = request.Get<Date>("value") ? moment(request.Get<Date>("value")).hours(0).seconds(0) : null;
        store.logTabModel = store.logTabModel.set("DateFrom", date ? date.toDate() : null);
    }

    private handleToDateChanged(request: LocalEvent, store: AdministrationStore): void {
        let date = request.Get<Date>("value") ? moment(request.Get<Date>("value")).hours(0).seconds(0) : null;
        store.logTabModel = store.logTabModel.set("DateTo", date ? date.toDate() : null);
    }

    private handleUserFilterTextChanged(request: LocalEvent, store: AdministrationStore): void {
        store.logTabModel = store.logTabModel.set("UserSearchText", request.Get<string>("value"));
        store.logTabModel = store.logTabModel.set("UsersFiltered", store.getUsersBasedOnQuery());
    }

    private handleActionFilterTextChanged(request: LocalEvent, store: AdministrationStore): void {
        store.logTabModel = store.logTabModel.set("ActionSearchText", request.Get<string>("value"));
        store.logTabModel = store.logTabModel.set("ActionTypeFiltered", store.getActionsBasedOnQuery());
    }

    private handleNbAccessTextChanged(request: LocalEvent, store: AdministrationStore): void {
        let nbAccess = request.Get<string>("value");
        if (IsNullOrWhiteSpace(nbAccess))
            nbAccess = null;
        store.logTabModel = store.logTabModel.set("NbAccess", nbAccess ? parseInt(nbAccess) : null);
    }

    private handleIntervalTextChanged(request: LocalEvent, store: AdministrationStore): void {
        let interval = request.Get<string>("value");
        if (IsNullOrWhiteSpace(interval))
            interval = null;
        store.logTabModel = store.logTabModel.set("Interval", interval ? parseInt(interval) : null);
    }

    private handleIntervalTypeChanged(request: LocalEvent, store: AdministrationStore): void {
        store.logTabModel = store.logTabModel.set("IntervalType", request.Get<LogIntervalType>("value"));
    }

    private handlePageChange(request: LocalEvent, store: AdministrationStore): void {
        var value = request.Get<number>("Page");
        store.logTabModel = store.logTabModel.set("SelectedPageNumber", value);
        this.loadAdminLogData(request, store);
    }

    private handleResulstPerPageChange(request: LocalEvent, store: AdministrationStore): void {
        store.logTabModel = store.logTabModel.set("SelectedPageNumber", 1);
        store.logTabModel = store.logTabModel.set("SelectedPageSizeOption", request.Get<number>("value"));
    }

    private columnSort(request: LocalEvent, store: AdministrationStore): void {
        let tableTarget = request.Get<MyleTableTarget>("target");
        if (tableTarget === MyleTableTarget.Myle_AdminLog_ColumnSortClick) {
            let currentSortCol = store.logTabModel.get("GridSortColumn");
            let sortDir = store.logTabModel.get("GridSortDirection");
            let sortCol = request.Get<LogSortColumn>("sortColumn");

            if (currentSortCol != sortCol) {
                sortDir = SortDirection.ASC;
            }
            else {
                if (sortDir == SortDirection.ASC)
                    sortDir = SortDirection.DESC;
                else
                    sortDir = SortDirection.ASC;
            }

            store.logTabModel = store.logTabModel.set("GridSortColumn", sortCol);
            store.logTabModel = store.logTabModel.set("GridSortDirection", sortDir);

            this.loadAdminLogData(request, store);
        }
    }

    private handleContainerClick(request: LocalEvent, store: AdministrationStore): void {
        if (store.logTabModel.get("ShowLogDisplaySettings") == true) {
            if (administrationStore.getLogDateRangeErrorMessage() == "")
                this.handleSearchSettingsOkClick(request, store);
        }
    }

    private columnOrderingPopup_Open(request: LocalEvent, store: AdministrationStore): void {
        let isOpen = store.logTabModel.get("ColumnOrderingPopupOpen");
        store.logTabModel = store.logTabModel.set("ColumnOrderingPopupOpen", !isOpen);
    }

    private handleOpenOntarioCdsExport(request: LocalEvent, store: AdministrationStore): void {
        store.logTabModel = store.logTabModel.set("IsOntarioCdsExportPopupOpen", true);
    }

    private handleCloseOntarioCdsExport(request: LocalEvent, store: AdministrationStore): void {
        store.logTabModel = store.logTabModel.set("IsOntarioCdsExportPopupOpen", false);
    }

    private handleOpenOntarioCdsImport(request: LocalEvent, store: AdministrationStore): void {
        store.logTabModel = store.logTabModel.set("IsOntarioCdsImportPopupOpen", true);
    }

    private handleCloseOntarioCdsImport(request: LocalEvent, store: AdministrationStore): void {
        store.logTabModel = store.logTabModel.set("IsOntarioCdsImportPopupOpen", false);
    }
}

export { AdministrationLogHandler };
